import { useState, useEffect } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Menu,
  MenuItem,
  Typography,
  Divider,
  CircularProgress,
  Tooltip
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLogout, useGetIdentity } from 'react-admin';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import SearchIcon from '@mui/icons-material/Search';
import CasinoIcon from '@mui/icons-material/Casino';
import AssessmentIcon from '@mui/icons-material/Assessment';
import BarChartIcon from '@mui/icons-material/BarChart';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PersonIcon from '@mui/icons-material/Person';
import EditIcon from '@mui/icons-material/Edit';
import LockIcon from '@mui/icons-material/Lock';
import LogoutIcon from '@mui/icons-material/Logout';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';

const SIDEBAR_WIDTH_OPEN = 260;
const SIDEBAR_WIDTH_CLOSED = 60;

const MENU_SECTIONS = [
  {
    key: 'player-account',
    text: 'Player Account',
    icon: <ManageAccountsIcon />,
    basePath: '/player-account',
    items: [
      { text: 'Player Account Search', path: '/player-account/search', icon: <SearchIcon /> },
      { text: 'Player Spin Search', path: '/player-account/spin-search', icon: <CasinoIcon /> }
    ]
  },
  {
    key: 'reports',
    text: 'Reports',
    icon: <AssessmentIcon />,
    basePath: '/reports',
    items: [
      { text: 'Agent Currency Report', path: '/reports/agent-currency', icon: <BarChartIcon /> },
      {
        text: 'Agent Daily Currency Report',
        path: '/reports/agent-daily-currency',
        icon: <TrendingUpIcon />
      }
    ]
  },
  {
    key: 'profile',
    text: 'Profile',
    icon: <PersonIcon />,
    basePath: '/profile',
    items: [
      { text: 'Edit Profile', path: '/profile/edit', icon: <EditIcon /> },
      { text: 'Change Password', path: '/profile/change-password', icon: <LockIcon /> },
      { text: 'Logout', action: 'logout', icon: <LogoutIcon /> }
    ]
  }
];

export const CustomLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const logout = useLogout();
  const { identity } = useGetIdentity();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [loggingOut, setLoggingOut] = useState(false);
  const [profileMenuAnchor, setProfileMenuAnchor] = useState(null);

  const getInitialExpandedSections = () => {
    const expanded = {};
    MENU_SECTIONS.forEach((section) => {
      expanded[section.key] = location.pathname.startsWith(section.basePath);
    });
    return expanded;
  };

  const [expandedSections, setExpandedSections] = useState(getInitialExpandedSections);

  useEffect(() => {
    setExpandedSections((prev) => {
      const next = { ...prev };
      MENU_SECTIONS.forEach((section) => {
        if (location.pathname.startsWith(section.basePath)) {
          next[section.key] = true;
        }
      });
      return next;
    });
  }, [location.pathname]);

  const handleLogout = async () => {
    setLoggingOut(true);
    setProfileMenuAnchor(null);
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
      setLoggingOut(false);
    }
  };

  const handleSectionToggle = (sectionKey) => {
    if (!sidebarOpen) {
      setSidebarOpen(true);
      setExpandedSections((prev) => ({ ...prev, [sectionKey]: true }));
    } else {
      setExpandedSections((prev) => ({ ...prev, [sectionKey]: !prev[sectionKey] }));
    }
  };

  const handleNavItem = (item) => {
    if (item.action === 'logout') {
      handleLogout();
    } else {
      navigate(item.path);
    }
  };

  const getCurrentPageTitle = () => {
    for (const section of MENU_SECTIONS) {
      for (const item of section.items) {
        if (item.path && location.pathname === item.path) return item.text;
      }
    }
    return 'Operator Portal';
  };

  const isItemActive = (path) => path && location.pathname === path;

  const operatorName = identity?.fullName || localStorage.getItem('operatorName') || 'Operator';

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Sidebar */}
      <Box
        sx={{
          width: sidebarOpen ? SIDEBAR_WIDTH_OPEN : SIDEBAR_WIDTH_CLOSED,
          flexShrink: 0,
          backgroundColor: '#1A1A1A',
          borderRight: '1px solid rgba(255, 255, 255, 0.08)',
          transition: 'width 0.3s ease',
          overflow: 'hidden',
          position: 'fixed',
          top: 0,
          left: 0,
          height: '100vh',
          zIndex: 1100,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Sidebar Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: sidebarOpen ? 'space-between' : 'center',
            px: sidebarOpen ? 2 : 0,
            height: '64px',
            borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
            flexShrink: 0
          }}
        >
          {sidebarOpen && (
            <Box
              component="img"
              src="/Assets/Logos/sidebar-logo.svg"
              alt="Operator Portal"
              sx={{ height: '36px', width: 'auto' }}
            />
          )}
          <IconButton
            onClick={() => setSidebarOpen(!sidebarOpen)}
            size="small"
            sx={{
              color: '#FFC107',
              backgroundColor: sidebarOpen ? 'transparent' : '#242424',
              border: sidebarOpen ? 'none' : '1px solid rgba(255, 255, 255, 0.1)',
              '&:hover': { backgroundColor: 'rgba(255, 193, 7, 0.1)' }
            }}
          >
            {sidebarOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </IconButton>
        </Box>

        {/* Menu */}
        <Box sx={{ flexGrow: 1, overflowY: 'auto', overflowX: 'hidden', pt: 1 }}>
          <List disablePadding>
            {MENU_SECTIONS.map((section) => {
              const isSectionActive = location.pathname.startsWith(section.basePath);
              const isExpanded = expandedSections[section.key];

              return (
                <Box key={section.key}>
                  {/* Section Header */}
                  <Tooltip title={!sidebarOpen ? section.text : ''} placement="right" arrow>
                    <ListItemButton
                      onClick={() => handleSectionToggle(section.key)}
                      sx={{
                        px: sidebarOpen ? 2 : 0,
                        py: 1.25,
                        justifyContent: sidebarOpen ? 'flex-start' : 'center',
                        backgroundColor: isSectionActive
                          ? 'rgba(255, 193, 7, 0.08)'
                          : 'transparent',
                        '&:hover': { backgroundColor: 'rgba(255, 193, 7, 0.08)' }
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          color: isSectionActive ? '#FFC107' : 'rgba(255,255,255,0.7)',
                          minWidth: sidebarOpen ? 40 : 'auto'
                        }}
                      >
                        {section.icon}
                      </ListItemIcon>
                      {sidebarOpen && (
                        <>
                          <ListItemText
                            primary={section.text}
                            sx={{
                              '& .MuiListItemText-primary': {
                                color: isSectionActive ? '#FFC107' : 'rgba(255,255,255,0.85)',
                                fontSize: '14px',
                                fontWeight: isSectionActive ? 600 : 500,
                                letterSpacing: '0.3px'
                              }
                            }}
                          />
                          {isExpanded ? (
                            <ExpandLessIcon sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 18 }} />
                          ) : (
                            <ExpandMoreIcon sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 18 }} />
                          )}
                        </>
                      )}
                    </ListItemButton>
                  </Tooltip>

                  {/* Sub Items */}
                  {sidebarOpen && (
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <List disablePadding>
                        {section.items.map((item) => {
                          const active = isItemActive(item.path);
                          return (
                            <ListItem key={item.text} disablePadding>
                              <ListItemButton
                                onClick={() => handleNavItem(item)}
                                disabled={item.action === 'logout' && loggingOut}
                                sx={{
                                  pl: 4,
                                  pr: 2,
                                  py: 1,
                                  backgroundColor: active
                                    ? 'rgba(255, 193, 7, 0.12)'
                                    : 'transparent',
                                  borderLeft: active
                                    ? '3px solid #FFC107'
                                    : '3px solid transparent',
                                  '&:hover': {
                                    backgroundColor: active
                                      ? 'rgba(255, 193, 7, 0.16)'
                                      : 'rgba(255, 193, 7, 0.06)'
                                  }
                                }}
                              >
                                <ListItemIcon
                                  sx={{
                                    color: active ? '#FFC107' : 'rgba(255,255,255,0.55)',
                                    minWidth: 36
                                  }}
                                >
                                  {item.action === 'logout' && loggingOut ? (
                                    <CircularProgress size={18} sx={{ color: '#FFC107' }} />
                                  ) : (
                                    item.icon
                                  )}
                                </ListItemIcon>
                                <ListItemText
                                  primary={item.text}
                                  sx={{
                                    '& .MuiListItemText-primary': {
                                      color: active ? '#FFC107' : 'rgba(255,255,255,0.7)',
                                      fontSize: '13px',
                                      fontWeight: active ? 600 : 400
                                    }
                                  }}
                                />
                              </ListItemButton>
                            </ListItem>
                          );
                        })}
                      </List>
                    </Collapse>
                  )}

                  <Divider sx={{ borderColor: 'rgba(255,255,255,0.04)', mx: 1 }} />
                </Box>
              );
            })}
          </List>
        </Box>
      </Box>

      {/* Main Content Area */}
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          marginLeft: sidebarOpen ? `${SIDEBAR_WIDTH_OPEN}px` : `${SIDEBAR_WIDTH_CLOSED}px`,
          transition: 'margin-left 0.3s ease'
        }}
      >
        {/* App Bar */}
        <AppBar
          position="sticky"
          top={0}
          sx={{
            backgroundColor: '#242424',
            borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
            boxShadow: 'none',
            zIndex: 1000
          }}
        >
          <Toolbar sx={{ minHeight: '64px !important' }}>
            {/* Page Title */}
            <Box sx={{ flexGrow: 1 }}>
              <Typography
                sx={{
                  color: '#FFC107',
                  fontSize: '20px',
                  fontWeight: 600,
                  letterSpacing: '0.3px'
                }}
              >
                {getCurrentPageTitle()}
              </Typography>
            </Box>

            {/* Operator Name */}
            <Typography
              sx={{
                color: 'rgba(255,255,255,0.7)',
                fontSize: '14px',
                mr: 1,
                display: { xs: 'none', sm: 'block' }
              }}
            >
              {operatorName}
            </Typography>

            {/* Profile Icon */}
            <IconButton
              onClick={(e) => setProfileMenuAnchor(e.currentTarget)}
              sx={{
                color: '#FFC107',
                '&:hover': { backgroundColor: 'rgba(255, 193, 7, 0.1)' }
              }}
            >
              <AccountCircleIcon sx={{ fontSize: 32 }} />
            </IconButton>

            {/* Profile Dropdown */}
            <Menu
              anchorEl={profileMenuAnchor}
              open={Boolean(profileMenuAnchor)}
              onClose={() => setProfileMenuAnchor(null)}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              PaperProps={{
                sx: {
                  backgroundColor: '#2A2A2A',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '8px',
                  mt: 0.5,
                  minWidth: 160
                }
              }}
            >
              <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                <Typography sx={{ color: '#fff', fontSize: '14px', fontWeight: 600 }}>
                  {operatorName}
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px' }}>
                  Operator
                </Typography>
              </Box>
              <MenuItem
                onClick={() => {
                  setProfileMenuAnchor(null);
                  navigate('/profile/edit');
                }}
                sx={{
                  color: 'rgba(255,255,255,0.8)',
                  fontSize: '14px',
                  py: 1.25,
                  '&:hover': { backgroundColor: 'rgba(255,193,7,0.08)', color: '#FFC107' }
                }}
              >
                <PersonIcon sx={{ fontSize: 18, mr: 1.5 }} />
                Profile
              </MenuItem>
              <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />
              <MenuItem
                onClick={handleLogout}
                disabled={loggingOut}
                sx={{
                  color: 'rgba(255,255,255,0.8)',
                  fontSize: '14px',
                  py: 1.25,
                  '&:hover': { backgroundColor: 'rgba(255,100,100,0.08)', color: '#ff6b6b' }
                }}
              >
                {loggingOut ? (
                  <CircularProgress size={18} sx={{ color: '#FFC107', mr: 1.5 }} />
                ) : (
                  <LogoutIcon sx={{ fontSize: 18, mr: 1.5 }} />
                )}
                {loggingOut ? 'Logging out...' : 'Logout'}
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        {/* Page Content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            px: 2,
            py: 2,
            backgroundColor: '#1A1A1A',
            minHeight: 'calc(100vh - 64px)',
            overflowY: 'auto'
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};
